import { useEffect, useRef, useState } from "react";
import { createMotionRecorder } from "../lib/motionRecorder.mjs";
import { createMotionRecordingStore, recordingKey } from "../lib/motionRecordingStore.mjs";

// Owned by WorkMode, not the optional briefing: closing that panel must not stop
// an explicitly started drive recording. Account/lesson changes do stop it.
export default function useMotionRecording({ ownerId, lessonId, stage }) {
  const key = ownerId && lessonId ? recordingKey(String(ownerId), String(lessonId)) : null;
  const [state, setState] = useState({ state: "loading" });
  const storage = useRef(null), controller = useRef(null), generation = useRef(0);
  const scope = useRef(key); scope.current = key;
  const getStore = () => {
    if (!storage.current) storage.current = createMotionRecordingStore(window);
    return storage.current;
  };
  useEffect(() => {
    const run = ++generation.current;
    controller.current = null;
    setState({ state: key ? "loading" : "unavailable" });
    let disposed = false;
    if (key) getStore().read(key).then(record => {
      if (!disposed && run === generation.current) setState(record
        ? { state: "saved", record, saved_records: record.saved_records, recovered: record.state === "recording" }
        : { state: "ready" });
    }).catch(error => { if (!disposed && run === generation.current) setState({ state: "error", error: error.message }); });
    return () => {
      disposed = true; generation.current++;
      const current = controller.current; controller.current = null;
      void current?.stop("lesson_or_account_left");
    };
  }, [key]);
  useEffect(() => {
    if (!["arrived", "in_progress"].includes(stage)) void controller.current?.stop("lesson_finished");
  }, [stage]);

  const start = async () => {
    if (!key || stage !== "arrived" || controller.current || state.state !== "ready") return;
    const run = generation.current;
    const next = createMotionRecorder({ env: window, store: getStore(), key,
      onState: update => { if (run === generation.current && scope.current === key) setState(update); } });
    controller.current = next;
    try {
      const active = await next.start();
      if (!active && run === generation.current) {
        controller.current = null;
        const record = await getStore().read(key);
        if (run === generation.current) setState(record ? { state: "saved", record, saved_records: record.saved_records } : { state: "ready" });
      }
    } catch (error) {
      if (run === generation.current) { controller.current = null; setState({ state: "ready", error: error.message }); }
    }
  };
  const stop = () => controller.current?.stop("user_stopped");
  const exportRecording = async () => {
    const run = generation.current;
    if (!key || stage === "in_progress" || ["permission", "recording", "saving"].includes(state.state)) return;
    try {
      const report = await getStore().export(key);
      if (run !== generation.current || scope.current !== key) return;
      const url = URL.createObjectURL(new Blob([JSON.stringify(report)], { type: "application/json" }));
      const link = document.createElement("a"); link.href = url; link.download = `ndg-motion-${report.id}.json`;
      document.body.appendChild(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) { if (run === generation.current) setState(old => ({ ...old, error: error.message })); }
  };
  const remove = async () => {
    const run = generation.current;
    if (!key || stage === "in_progress" || ["permission", "recording", "saving"].includes(state.state)) return;
    try {
      await getStore().remove(key);
      if (run === generation.current && scope.current === key) { controller.current = null; setState({ state: "ready" }); }
    } catch (error) { if (run === generation.current) setState(old => ({ ...old, error: error.message })); }
  };
  return { ...state, canStart: stage === "arrived" && state.state === "ready",
    canManage: stage !== "in_progress" && ["saved", "error"].includes(state.state), start, stop, exportRecording, remove };
}
